import type { TSchema } from "@sinclair/typebox";
import { PARAM_KEY, type ParamKind } from "../schema-util";

/** Wire form of query/path parameters: strings, or lists of strings for repeated keys. */
export type WireParams = Record<string, string | string[]>;

function kindOf(schema: TSchema | undefined): ParamKind | undefined {
  return (schema as Record<string, unknown> | undefined)?.[PARAM_KEY] as ParamKind | undefined;
}

function propertiesOf(schema: TSchema): Record<string, TSchema> {
  return ((schema as { properties?: Record<string, TSchema> }).properties ?? {}) as Record<string, TSchema>;
}

/**
 * Wire strings -> handler values, for an object schema whose properties may carry `x-decode`.
 * Call it only after `Value.Check(schema, wire)` succeeded. Unknown keys pass through untouched.
 */
export function decodeParams(schema: TSchema, wire: WireParams): Record<string, unknown> {
  const props = propertiesOf(schema);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(wire)) {
    switch (kindOf(props[key])) {
      case "int":
      case "float":
        out[key] = Number(value);
        break;
      case "bool":
        out[key] = value === "true";
        break;
      case "list":
        out[key] = Array.isArray(value) ? value : [value];
        break;
      default:
        out[key] = value;
    }
  }
  return out;
}

/** Handler/client values -> wire strings (what goes into a URL). `undefined` values are dropped. */
export function encodeParams(
  schema: TSchema | undefined,
  values: Record<string, unknown> | undefined,
): WireParams {
  const props = schema ? propertiesOf(schema) : {};
  const out: WireParams = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (value === undefined || value === null) continue;
    switch (kindOf(props[key])) {
      case "list": {
        const list = (Array.isArray(value) ? value : [value]).map(String);
        if (list.length > 0) out[key] = list;
        break;
      }
      case "bool":
        out[key] = value ? "true" : "false";
        break;
      default:
        out[key] = String(value);
    }
  }
  return out;
}

/** `/api/galleries/:gid` + `{ gid: 5 }` -> `/api/galleries/5` (values are URL-encoded). */
export function fillPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z]+)/g, (_, key: string) => {
    const v = params?.[key];
    if (v === undefined) throw new Error(`missing path parameter ${key} for ${path}`);
    return encodeURIComponent(String(v));
  });
}

/** `{ tag: ["a","b"], limit: "5" }` -> `tag=a&tag=b&limit=5` */
export function toQueryString(wire: WireParams): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(wire)) {
    for (const v of Array.isArray(value) ? value : [value]) q.append(key, v);
  }
  return q.toString();
}
